import React, { useState, useEffect, useContext } from 'react';
import createAuth0Client from '@auth0/auth0-spa-js';

const DEFAULT_REDIRECT_CALLBACK = () => window.history.replaceState({}, document.title, window.location.pathname);

export const Auth0Context = React.createContext({});
// eslint-disable-next-line
export const useAuth0 = () => useContext(Auth0Context);
export const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}: {
  children: React.ReactNode;
  onRedirectCallback?: (appState: { targetUrl: string | URL | null | undefined }) => void;
  [key: string]: any;
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [user, setUser] = useState();
  const [authClient, setAuthClient] = useState();
  // Should be removed, since it's replaced by `authClient`. Kept here for backward compatibility only.
  // TODO: remove `auth0Client` once it's not used anymore in Interaxo applications.
  const [auth0Client, setAuth0] = useState();
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client(initOptions);
      setAuth0(auth0FromHook);
      setAuthClient(auth0FromHook);

      if (window.location.search.includes('code=')) {
        const { appState } = await auth0FromHook.handleRedirectCallback();
        localStorage.setItem('isAuthenticated', 'true');
        onRedirectCallback(appState);
      }

      const isAuthenticated = await auth0FromHook.isAuthenticated();

      setIsAuthenticated(isAuthenticated);

      if (isAuthenticated) {
        const user = await auth0FromHook.getUser();
        setUser(user);
      }

      setLoading(false);
    };
    initAuth0();
    // eslint-disable-next-line
  }, []);

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true);
    try {
      await authClient.loginWithPopup(params);
    } catch (error) {
      console.error(error);
    } finally {
      setPopupOpen(false);
    }
    const user = await authClient.getUser();
    setUser(user);
    setIsAuthenticated(true);
  };

  const handleRedirectCallback = async () => {
    setLoading(true);
    await authClient.handleRedirectCallback();
    const user = await authClient.getUser();
    setLoading(false);
    setIsAuthenticated(true);
    setUser(user);
  };

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        popupOpen,
        auth0Client,
        authClient,
        loginWithPopup,
        handleRedirectCallback,
        getIdTokenClaims: (...p: any) => authClient.getIdTokenClaims(...p),
        loginWithRedirect: (...p: any) => authClient.loginWithRedirect(...p),
        getTokenSilently: (...p: any) => authClient.getTokenSilently(...p),
        getTokenWithPopup: (...p: any) => authClient.getTokenWithPopup(...p),
        logout: (...p: any) => authClient.logout(...p),
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
